import CmpCommandName from './CmpCommandName';
import processGetConsentDataCommand from './processCommand/processGetConsentDataCommand';
import processGetVendorConsentsCommand from './processCommand/processGetVendorConsentsCommand';
import processPingCommand from './processCommand/processPingCommand';
import { logUsage } from 'owa-analytics';

export default function initializeAdCmp(): Promise<void> {
    // References:
    // Public spec: https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/blob/master/CMP%20JS%20API%20v1.1%20Final.md
    // Appnexus reference: https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework

    // Add a hidden CmpLocator iFrame so that the Ads running in the cross-domain iFrame could know __CMP will be defined
    addCmpLocatorFrame();

    // Every consent manager MUST provide the following API:
    // __cmp(Command, Parameter, Callback)
    (window as any).__cmp =
        (window as any).__cmp ||
        function (commandName: string, parameter: any, onCallback: any) {
            switch (commandName) {
                case CmpCommandName.getConsentData:
                    processGetConsentDataCommand(onCallback);
                    break;

                case CmpCommandName.ping:
                    processPingCommand(onCallback);
                    break;

                case CmpCommandName.getVendorConsents:
                    processGetVendorConsentsCommand(parameter, onCallback);
                    break;
            }
        };

    // Register the event handler to listen the postMessage from the Ads iFrame
    // the handler would call the __cmp command and post the result back to the origin
    if (!(window as any).__cmp.msgHandler) {
        (window as any).__cmp.msgHandler = cmpMsgHandler;
        window.addEventListener('message', cmpMsgHandler, false);
    }

    return Promise.resolve();
}

function addCmpLocatorFrame() {
    const cmpFrameName = '__cmpLocator';
    if (!window.frames[cmpFrameName]) {
        const iframe = window.document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.name = cmpFrameName;
        window.document.body.appendChild(iframe);
    }
}

function cmpMsgHandler(event: any) {
    // Only accept the event coming from AppNexus
    // So far we only know files in AppNexus hosting iFrame would call Outlook.com __cmp to get the consent data
    // We are trying to limit this scope as smaller as possible
    if (event.origin != 'https://acdn.adnxs.com') {
        return;
    }

    const eventTypeIsString = typeof event.data === 'string';

    let eventInJson;
    if (eventTypeIsString) {
        try {
            eventInJson = JSON.parse(event.data);
        } catch (ex) {}
    } else {
        eventInJson = event.data;
    }

    if (eventInJson?.__cmpCall) {
        const eventCmpCall = eventInJson.__cmpCall;

        (window as any).__cmp(eventCmpCall.command, eventCmpCall.parameter, function (
            retValue: any
        ) {
            const returnMsg = {
                __cmpReturn: {
                    callId: eventCmpCall.callId,
                    command: eventCmpCall.command,
                    returnValue: retValue,
                },
            };

            if (event.source) {
                event.source.postMessage(
                    eventTypeIsString ? JSON.stringify(returnMsg) : returnMsg,
                    event.origin
                );
            } else {
                // We are not able to call JSON.stringify over the whole event object as it has the Circular reference.
                // We are trying to log some information from the event object to see why the source is null for one event
                logUsage('cmpEmptyEventSource', {
                    eventtype: event.type,
                    eventdata: JSON.stringify(event.data),
                });
            }
        });
    }
}
